import { useState } from 'react';
import SEOHead from '@/components/base/SEOHead';
import Breadcrumb from '@/components/base/Breadcrumb';
import { aboutInfo, testimonials, faqItems } from '@/mocks/comics';

const FORM_URL = 'https://readdy.ai/api/form/d9omtp2qs38sj6jc7bm0';
const CONTACT_FORM_ID = 'yaojing-contact-form';

export default function About() {
  const [formSuccess, setFormSuccess] = useState(false);
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess(false);

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value.trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    const formData = new FormData(form);
    formData.delete('website_alt');

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        setFormError(msg);
      }
    } catch {
      setFormError('网络错误，请稍后重试');
    }
  };

  const aboutSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: '关于妖精动漫',
    description: aboutInfo.description,
    url: 'https://yaojingdongman.com/about',
    mainEntity: {
      '@type': 'Organization',
      name: aboutInfo.brandName,
      foundingDate: aboutInfo.founded,
      description: aboutInfo.description,
      url: 'https://yaojingdongman.com',
      contactPoint: {
        '@type': 'ContactPoint',
        email: aboutInfo.contact.email,
        contactType: 'customer service',
      },
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  return (
    <>
      <SEOHead
        title="关于妖精动漫 | 品牌介绍与联系方式 | 妖精动漫免费漫画阅读平台"
        description={`${aboutInfo.description} 妖精动漫致力于为漫画爱好者提供韩漫、日漫、国漫的免费在线阅读体验。`}
        keywords="妖精动漫,关于妖精动漫,妖精动漫联系方式,妖精动漫品牌介绍,免费漫画平台"
        canonicalUrl="https://yaojingdongman.com/about"
        schema={aboutSchema}
      />

      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '关于我们' }]} />

        {/* Hero */}
        <div className="text-center mb-14">
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-900 mb-4">
            关于妖精动漫
          </h1>
          <p className="text-foreground-500 max-w-2xl mx-auto text-sm md:text-base leading-relaxed">
            {aboutInfo.description}
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
          {aboutInfo.stats.map((stat) => (
            <div
              key={stat.label}
              className="text-center p-5 rounded-xl bg-background-100 border border-background-200/70"
            >
              <div className="text-2xl md:text-3xl font-heading font-bold text-primary-500 mb-1">
                {stat.value}
              </div>
              <div className="text-sm text-foreground-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Brand Story */}
        <section className="mb-16">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-6 text-center">
              品牌故事
            </h2>
            <p className="text-sm text-foreground-600 leading-relaxed mb-4">
              {aboutInfo.story}
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
              {aboutInfo.values.map((value) => (
                <div
                  key={value.title}
                  className="p-5 rounded-xl bg-background-100 border border-background-200/70 flex gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 flex-shrink-0">
                    <i className={`${value.icon} text-lg`}></i>
                  </div>
                  <div>
                    <h4 className="text-sm font-semibold text-foreground-900 mb-1 whitespace-nowrap">
                      {value.title}
                    </h4>
                    <p className="text-xs text-foreground-500">{value.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="mb-16">
          <div className="text-center mb-10">
            <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
              用户口碑
            </h2>
            <p className="text-sm text-foreground-500">听听漫画迷们怎么说</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="p-5 rounded-xl bg-background-100 border border-background-200/70"
              >
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <i key={i} className="ri-star-fill text-accent-500 text-xs"></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  &ldquo;{t.content}&rdquo;
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={t.avatar}
                    alt={t.name}
                    className="w-9 h-9 rounded-full object-cover"
                    width={36}
                    height={36}
                  />
                  <div>
                    <div className="text-sm font-medium text-foreground-900">{t.name}</div>
                    <div className="text-xs text-foreground-400">{t.date}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Contact */}
        <section className="mb-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
                联系我们
              </h2>
              <p className="text-sm text-foreground-500">有任何问题或建议，欢迎随时联系我们</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-mail-line"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-0.5">邮箱</div>
                <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.email}</div>
              </div>
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-wechat-line"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-0.5">微信</div>
                <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.wechat}</div>
              </div>
              <div className="p-4 rounded-xl bg-background-100 border border-background-200/70 text-center">
                <div className="w-10 h-10 mx-auto mb-2 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600">
                  <i className="ri-weibo-line"></i>
                </div>
                <div className="text-xs text-foreground-500 mb-0.5">微博</div>
                <div className="text-sm font-medium text-foreground-900">{aboutInfo.contact.weibo}</div>
              </div>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4"
              data-readdy-form
              id={CONTACT_FORM_ID}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱 <span className="text-accent-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言 <span className="text-accent-500">*</span>
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-4 py-2.5 text-sm rounded-lg bg-background-100 border border-background-200/70 text-foreground-800 placeholder-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
              </div>

              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />

              <div>
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium text-sm hover:bg-primary-600 transition-all duration-200 cursor-pointer whitespace-nowrap"
                >
                  提交留言
                </button>
              </div>

              {formSuccess && (
                <div className="p-3 rounded-lg bg-accent-100/70 text-accent-700 text-sm">
                  提交成功！我们会尽快回复您。
                </div>
              )}
              {formError && (
                <div className="p-3 rounded-lg bg-red-50 text-red-600 text-sm">
                  {formError}
                </div>
              )}
            </form>
          </div>
        </section>

        {/* FAQ on About */}
        <section>
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-heading font-bold text-foreground-900 mb-3">
                常见问题
              </h2>
            </div>
            <div className="space-y-3">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className="p-4 md:p-5 rounded-xl bg-background-100 border border-background-200/70"
                >
                  <h4 className="text-sm font-semibold text-foreground-900 mb-2">{item.question}</h4>
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}